import React, { useState, useEffect, useRef } from 'react';
import { cloneDeep } from 'lodash';

import DropdownWrapper from '../../base/dropdowns/dropdown-wrapper';
import { SearchBar } from '../../ui/search';
import { useOutsideClick } from '../../../utils/custom-hooks';

import './styles/confirm-dropdown.scss';

const ConfirmDropdown = ({
  items,
  updateItems,
  itemType,
  hasSearch = false,
  confirmLabel = null,
  defaultSelectNone = false,
  defaultHideDeselectAll = false,
  labelOverride = null,
}) => {
  const itemTypeParts = itemType.split('-');
  const dropdown = useRef(false);

  const [invisibleOptions, setInvisibleOptions] = useState([]);
  const [didMount, setDidMount] = useState(false);
  const [visibleOptions, setVisibleOptions] = useState(checkIfAllSelected(items));
  const [query, setQuery] = useState('');

  useEffect(() => setDidMount(true), []);

  useOutsideClick(
    dropdown,
    () => {
      cancelChanges();
    },
    () => {},
    [items],
  );

  useEffect(() => {
    cancelChanges();
  }, [items]);

  useEffect(() => {
    if (didMount) {
      filterOptions();
    }
  }, [query]);

  function cancelChanges() {
    setVisibleOptions(checkIfAllSelected(items));
    setInvisibleOptions([]);
  }

  function checkIfAllSelected(options) {
    const newOptions = cloneDeep(options);
    if (defaultSelectNone && newOptions.every(option => option.selected)) {
      newOptions.map(option => (option.selected = false));
    }
    return newOptions;
  }

  function changeAll(selected) {
    const newOptions = cloneDeep(invisibleOptions);
    newOptions.map(option => (option.selected = selected));
    setInvisibleOptions(newOptions);

    const newVisibleOptions = cloneDeep(visibleOptions);
    newVisibleOptions.map(option => (option.selected = selected));
    setVisibleOptions(newVisibleOptions);
  }

  function selectOption(clickedOption) {
    const newVisibleOptions = cloneDeep(visibleOptions);

    const visibleOption = newVisibleOptions.find(option => clickedOption.value === option.value);
    visibleOption.selected = !visibleOption.selected;

    setVisibleOptions(newVisibleOptions);
  }

  function getLabel() {
    const selected = items.reduce((acc, option) => {
      return option.selected ? [...acc, option.label] : acc;
    }, []);

    const oneselected = selected.length === 1;
    const allSelected = !selected.length || selected.length === items.length;
    return oneselected
      ? selected[0]
      : `${allSelected ? 'All' : selected.length}
			${itemTypeParts[itemTypeParts.length - 1]}`;
  }

  function handleApply() {
    const newItems = cloneDeep(items);

    invisibleOptions.forEach(option => {
      const newItem = newItems.find(item => item.value === option.value);
      newItem.selected = option.selected;
    });

    visibleOptions.forEach(option => {
      const newItem = newItems.find(item => item.value === option.value);
      newItem.selected = option.selected;
    });

    if (newItems.every(item => !item.selected)) {
      newItems.map(item => (item.selected = true));
    }
    updateItems(newItems);
  }

  function filterOptions() {
    const newItems = cloneDeep(items);
    const newVisibleOptions = [];
    const newInvisibleOptions = [];

    newItems.forEach(item => {
      const option =
        visibleOptions.find(option => option.value === item.value) ||
        invisibleOptions.find(option => option.value === item.value);
      item.selected = option.selected;

      if (item.label.toLowerCase().includes(query.toLowerCase())) {
        newVisibleOptions.push(item);
      } else {
        newInvisibleOptions.push(item);
      }
    });

    setInvisibleOptions(newInvisibleOptions);
    setVisibleOptions(newVisibleOptions);
  }

  const searchBar = hasSearch ? <SearchBar placeholder="Search" onChange={setQuery} /> : null;

  const deselectAllButton =
    !defaultHideDeselectAll || visibleOptions.every(option => option.selected) ? (
      <>
        <span className="pipe">|</span>
        <span className="deselect-all-btn" onClick={() => changeAll(false)}>
          Deselect All
        </span>
      </>
    ) : null;

  return (
    <div className={`confirm-dropdown ${itemType}`} ref={node => (dropdown.current = node)}>
      <DropdownWrapper label={labelOverride ? labelOverride(items) : getLabel()}>
        <div onClick={e => e.stopPropagation()}>{searchBar}</div>
        <div className="change-all-controls" onClick={e => e.stopPropagation()}>
          <span className="select-all-btn" onClick={() => changeAll(true)}>
            Select All
          </span>
          {deselectAllButton}
        </div>

        <div className="options" onClick={e => e.stopPropagation()}>
          {visibleOptions.map((option, index) => (
            <li key={index} className="dropdown-item" onClick={() => selectOption(option)}>
              <label>
                <span className={`checkbox ${option.selected ? 'selected' : ''}`}></span>
                {option.label}
              </label>
            </li>
          ))}
        </div>

        <div className="confirm-controls">
          <button className="apply-btn" onClick={handleApply}>
            {confirmLabel || 'Apply'}
          </button>
          <button onClick={cancelChanges}>Cancel</button>
        </div>
      </DropdownWrapper>
    </div>
  );
};

export default ConfirmDropdown;
